import { useState, useEffect } from 'react';
import { X, Cookie, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

interface CookiePreferences {
  essential: boolean;
  functional: boolean;
  analytics: boolean;
}

const COOKIE_CONSENT_KEY = 'pdpo-assist-cookie-consent';

export function CookieConsentBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always required
    functional: true,
    analytics: false,
  });

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay to prevent flash on initial load
      const timer = setTimeout(() => setShowBanner(true), 500);
      return () => clearTimeout(timer);
    } else {
      try {
        const savedPreferences = JSON.parse(consent);
        setPreferences(savedPreferences);
      } catch (e) {
        console.error('Error parsing cookie consent:', e);
      }
    }
  }, []);

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    localStorage.setItem('pdpo-assist-cookie-consent-date', new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    const allAccepted = {
      essential: true,
      functional: true,
      analytics: true,
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const acceptSelected = () => {
    saveConsent(preferences);
  };

  const rejectNonEssential = () => {
    const essentialOnly = {
      essential: true,
      functional: false,
      analytics: false,
    };
    setPreferences(essentialOnly);
    saveConsent(essentialOnly);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40" />
      
      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
            {!showSettings ? (
              // Main Banner
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Cookie className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      We use cookies
                    </h3>
                    <p className="text-slate-600 text-sm mb-4">
                      We use cookies to provide essential functionality, remember your preferences, 
                      and analyse how you use our platform. By clicking "Accept All", you consent to 
                      our use of cookies. You can customise your preferences or learn more in our{' '}
                      <Link to="/cookies" className="text-blue-600 hover:underline">
                        Cookie Policy
                      </Link>.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={acceptAll}
                        className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                      >
                        Accept All
                      </button>
                      <button
                        onClick={rejectNonEssential}
                        className="px-5 py-2.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition font-medium"
                      >
                        Essential Only
                      </button>
                      <button
                        onClick={() => setShowSettings(true)}
                        className="px-5 py-2.5 text-slate-600 hover:text-slate-900 transition font-medium flex items-center gap-2"
                      >
                        <Settings className="w-4 h-4" />
                        Customise
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Settings Panel
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-900">Cookie Preferences</h3>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="p-2 text-slate-400 hover:text-slate-600 transition"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  {/* Essential */}
                  <div className="flex items-start justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex-1 mr-4">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-slate-900">Essential Cookies</h4>
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                          Required
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">
                        Required for the platform to function. Includes authentication, session management, 
                        and security features.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      className="w-5 h-5 rounded border-slate-300 text-blue-600 cursor-not-allowed mt-1"
                    />
                  </div>

                  {/* Functional */}
                  <div className="flex items-start justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex-1 mr-4">
                      <h4 className="font-medium text-slate-900 mb-1">Functional Cookies</h4>
                      <p className="text-sm text-slate-600">
                        Remember your preferences such as theme settings and UI customisations for a 
                        better experience.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={preferences.functional}
                      onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                      className="w-5 h-5 rounded border-slate-300 text-blue-600 cursor-pointer mt-1"
                    />
                  </div>

                  {/* Analytics */}
                  <div className="flex items-start justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex-1 mr-4">
                      <h4 className="font-medium text-slate-900 mb-1">Analytics Cookies</h4>
                      <p className="text-sm text-slate-600">
                        Help us understand how you use the platform so we can improve it. No personal 
                        data is shared with third parties for advertising.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                      className="w-5 h-5 rounded border-slate-300 text-blue-600 cursor-pointer mt-1"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={rejectNonEssential}
                    className="px-5 py-2.5 text-slate-600 hover:text-slate-900 transition font-medium"
                  >
                    Reject All
                  </button>
                  <button
                    onClick={acceptSelected}
                    className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                  >
                    Save Preferences
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// Hook to check cookie consent status
export function useCookieConsent() {
  const [consent, setConsent] = useState<CookiePreferences | null>(null);

  useEffect(() => {
    const saved = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (saved) {
      try {
        setConsent(JSON.parse(saved));
      } catch {
        setConsent(null);
      }
    }
  }, []);

  return consent;
}

// Function to reset cookie consent (for testing or settings page)
export function resetCookieConsent() {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  localStorage.removeItem('pdpo-assist-cookie-consent-date');
  window.location.reload();
}
